import React from 'react';
import clsx from 'clsx';
import type { WizardQuestion, BranchAction, ChoiceOption } from './types';

interface Props {
  question: WizardQuestion;
  onChange: (q: WizardQuestion) => void;
}

const ACTION_OPTIONS: { value: BranchAction; label: string; desc: string }[] = [
  { value: 'handoff', label: 'Send to a person', desc: 'Hand off to your team' },
  { value: 'end', label: 'Send a final message and end', desc: 'Bot says goodbye and closes the conversation' },
];

function updateOption(options: ChoiceOption[], id: string, patch: Partial<ChoiceOption>): ChoiceOption[] {
  return options.map(o => o.id === id ? { ...o, ...patch } : o);
}

export const WizardBranchEditor: React.FC<Props> = ({ question, onChange }) => {
  const options = question.options ?? [];

  const setOptionAction = (id: string, action: BranchAction) => {
    onChange({ ...question, options: updateOption(options, id, { action }) });
  };

  const setOptionEndMessage = (id: string, endMessage: string) => {
    onChange({ ...question, options: updateOption(options, id, { endMessage }) });
  };

  if (options.length === 0) {
    return (
      <div className="text-sm text-text-muted">No options to configure.</div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-text-secondary font-medium">
        "{question.prompt || 'Your question'}"
      </div>

      {options.map((opt, idx) => (
        <div
          key={opt.id}
          className="rounded-xl border border-border bg-surface p-4 space-y-3"
        >
          <div className="flex items-center gap-2">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-surface-alt text-[10px] font-bold text-text-muted">
              {idx + 1}
            </div>
            <span className="text-sm font-semibold text-text-primary">
              {opt.label || `Option ${idx + 1}`}
            </span>
          </div>

          <div className="text-[12px] font-medium text-text-muted mb-1">What should happen next?</div>

          <div className="flex flex-col gap-2">
            {ACTION_OPTIONS.map(ao => (
              <button
                key={ao.value}
                type="button"
                onClick={() => setOptionAction(opt.id, ao.value)}
                className={clsx(
                  'flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-all',
                  opt.action === ao.value
                    ? 'border-primary bg-primary-soft/60'
                    : 'border-border bg-surface-alt hover:border-primary/40',
                )}
              >
                <div
                  className={clsx(
                    'mt-0.5 h-3.5 w-3.5 shrink-0 rounded-full border-2 transition-colors',
                    opt.action === ao.value
                      ? 'border-primary bg-primary'
                      : 'border-border-strong',
                  )}
                />
                <div>
                  <div className={clsx(
                    'text-xs font-semibold',
                    opt.action === ao.value ? 'text-primary' : 'text-text-primary',
                  )}>
                    {ao.label}
                  </div>
                  <div className="text-[11px] text-text-muted">{ao.desc}</div>
                </div>
              </button>
            ))}
          </div>

          {opt.action === 'end' && (
            <div>
              <label className="mb-1 block text-[12px] font-medium text-text-secondary">
                Final message (optional)
              </label>
              <textarea
                rows={2}
                value={opt.endMessage ?? ''}
                onChange={e => setOptionEndMessage(opt.id, e.target.value)}
                placeholder="e.g. Thanks! We'll be in touch soon."
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary resize-none transition-colors"
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
